/**
 * Composant Layout avec système de grille CSS 8 colonnes
 * Gère les différents fractionnements : 8, 2-6, 4-4
 */

import { LitElement, html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { isMobile, onResize } from "../utils/responsive";
import widgetLayoutStyles from "../styles/widget-layout.css?inline";

export type LayoutFraction =
  | "full" // 8 colonnes
  | "filters-content" // 2-6 (filtres + contenu)
  | "split"; // 4-4 (contenu + contenu)

export interface WidgetLayoutConfig {
  fraction: LayoutFraction;
  showFilters?: boolean;
  headerSlot?: string;
  filtersSlot?: string;
  contentSlot?: string;
  mapSlot?: string;
  listSlot?: string;
  agendaSlot?: string;
}

@customElement("apidae-widget-layout")
export class WidgetLayout extends LitElement {
  static styles: CSSResultGroup = unsafeCSS(widgetLayoutStyles);

  @property({ type: Object })
  config: WidgetLayoutConfig = {
    fraction: "full",
  };

  @state()
  private isMobileMode: boolean = false;

  private resizeUnsubscribe?: () => void;

  connectedCallback() {
    super.connectedCallback();
    this.isMobileMode = isMobile();
    this.resizeUnsubscribe = onResize(
      (mobile) => {
        this.isMobileMode = mobile;
      },
      { immediate: true },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeUnsubscribe) {
      this.resizeUnsubscribe();
    }
  }

  private getGridTemplateColumns(): string {
    const { fraction } = this.config;

    if (this.isMobileMode) {
      // En mode mobile, tout est en colonne
      return "1fr";
    }

    switch (fraction) {
      case "full":
        return "repeat(8, 1fr)";
      case "filters-content":
        return "repeat(2, 1fr) repeat(6, 1fr)";
      case "split":
        return "repeat(4, 1fr) repeat(4, 1fr)";
      default:
        return "repeat(8, 1fr)";
    }
  }

  private getGridAreas(): string {
    const { fraction, showFilters } = this.config;

    if (this.isMobileMode) {
      // En mode mobile, tout est empilé
      return `
        "header"
        "filters"
        "content"
        "map"
        "list"
        "agenda"
      `;
    }

    // Si on a des filtres, le header est toujours à droite des filtres (2 colonnes filtres, 6 colonnes header)
    // Sinon, le header prend toute la largeur (8 colonnes)
    const headerArea = showFilters
      ? "filters filters header header header header header header"
      : "header header header header header header header header";

    switch (fraction) {
      case "full":
        // En full, on peut avoir list, agenda, content ou map
        const hasList = this.config.listSlot;
        const hasAgenda = this.config.agendaSlot;
        const hasMap = this.config.mapSlot;

        if (hasList) {
          return `
            "${headerArea}"
            "list list list list list list list list"
          `;
        } else if (hasAgenda) {
          return `
            "${headerArea}"
            "agenda agenda agenda agenda agenda agenda agenda agenda"
          `;
        } else if (hasMap) {
          return `
            "${headerArea}"
            "map map map map map map map map"
          `;
        } else {
          return `
            "${headerArea}"
            "content content content content content content content content"
          `;
        }
      case "filters-content":
        // Toujours avec filtres dans ce cas
        // On peut avoir list, agenda, content ou map
        const hasListWithFilters = this.config.listSlot;
        const hasAgendaWithFilters = this.config.agendaSlot;
        const hasMapWithFilters = this.config.mapSlot;

        if (hasListWithFilters) {
          return `
            "${headerArea}"
            "filters filters list list list list list list"
          `;
        } else if (hasAgendaWithFilters) {
          return `
            "${headerArea}"
            "filters filters agenda agenda agenda agenda agenda agenda"
          `;
        } else if (hasMapWithFilters) {
          return `
            "${headerArea}"
            "filters filters map map map map map map"
          `;
        } else {
          return `
            "${headerArea}"
            "filters filters content content content content content content"
          `;
        }
      case "split":
        // En split, on ne peut pas avoir de filtres (4-4 seulement)
        // On peut avoir list+map ou agenda+map
        const hasListAndMap = this.config.listSlot && this.config.mapSlot;
        const hasAgendaAndMap = this.config.agendaSlot && this.config.mapSlot;

        if (hasListAndMap) {
          // list (4) + map (4)
          return `
            "${headerArea}"
            "list list list list map map map map"
          `;
        } else if (hasAgendaAndMap) {
          // agenda (4) + map (4)
          return `
            "${headerArea}"
            "agenda agenda agenda agenda map map map map"
          `;
        } else {
          // Fallback : content (4) + map (4)
          return `
            "${headerArea}"
            "content content content content map map map map"
          `;
        }
      default:
        return `
          "${headerArea}"
          "content content content content content content content content"
        `;
    }
  }

  render() {
    const {
      showFilters,
      filtersSlot,
      contentSlot,
      mapSlot,
      listSlot,
      agendaSlot,
      headerSlot,
    } = this.config;

    return html`
      <div
        class="widget-layout ${this.isMobileMode ? "mobile" : ""}"
        style="grid-template-columns: ${this.getGridTemplateColumns()}; grid-template-areas: ${this.getGridAreas()}; grid-template-rows: auto 1fr;"
      >
        ${headerSlot
          ? html`
              <div class="layout-header" data-slot="${headerSlot}">
                <slot name="${headerSlot}"></slot>
              </div>
            `
          : ""}
        ${showFilters && filtersSlot
          ? html`
              <div class="layout-filters" data-slot="${filtersSlot}">
                <slot name="${filtersSlot}"></slot>
              </div>
            `
          : ""}
        ${contentSlot
          ? html`
              <div class="layout-content" data-slot="${contentSlot}">
                <slot name="${contentSlot}"></slot>
              </div>
            `
          : ""}
        ${mapSlot
          ? html`
              <div class="layout-map" data-slot="${mapSlot}">
                <slot name="${mapSlot}"></slot>
              </div>
            `
          : ""}
        ${listSlot
          ? html`
              <div class="layout-list" data-slot="${listSlot}">
                <slot name="${listSlot}"></slot>
              </div>
            `
          : ""}
        ${agendaSlot
          ? html`
              <div class="layout-agenda" data-slot="${agendaSlot}">
                <slot name="${agendaSlot}"></slot>
              </div>
            `
          : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-layout": WidgetLayout;
  }
}
